import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateFn, RouterStateSnapshot } from '@angular/router';
import { AuthGuard, AuthorizationSelectors } from '@next/core-lib/authorization';
import { UserSelectors, UserState } from '@next/core-lib/user';
import { Store } from '@ngrx/store';
import { combineLatest, filter, map, take } from 'rxjs';

/**
 * Guard to check if the user has global admin rights for a specific application key.
 * @param appKey The application key to check admin rights for.
 * @returns A CanActivateFn that returns true if the user has global admin rights for the specified application key.
 */
export function globalAdminForAppKeyGuard(appKey: string): CanActivateFn {
  return (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
    const store = inject<Store<UserState>>(Store);
    const authGuard = inject(AuthGuard);

    return combineLatest([
      store.select(UserSelectors.selectUserInitialized),
      store.select(AuthorizationSelectors.hasGlobalAdministratorRoleForApplicationKey(appKey)).pipe(filter((val) => val !== undefined)),
      authGuard.canActivate(route, state),
    ]).pipe(
      map(([userInitialized, hasRole, isAuthenticated]) => hasRole && isAuthenticated && userInitialized),
      take(1),
    );
  };
}
